import React, { useState, useRef, useEffect } from 'react';
import { Canvas, FabricText, Rect, Group, Line, FabricImage, Gradient, Shadow } from 'fabric';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Download, Sparkles, Calendar, DollarSign, Trophy, Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function FlyersIA() {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  
  const [fecha, setFecha] = useState({
    dia: new Date().getDate().toString().padStart(2, '0'),
    mes: new Date().toLocaleDateString('es-ES', { month: 'long' }).toUpperCase(),
    diaSemana: new Date().toLocaleDateString('es-ES', { weekday: 'long' }).toUpperCase()
  });
  
  const [eventos, setEventos] = useState([
    { hora: '09:00', nombre: 'BOXING KO', premio: 600, buyin: 6, destacado: false },
    { hora: '09:00', nombre: 'BINGO', premio: 1800, buyin: 12, destacado: false },
    { hora: '15:00', nombre: 'BOMB PKO', premio: 27000, buyin: 180, destacado: true },
    { hora: '18:00', nombre: 'SUPER UNION', premio: 60000, buyin: 400, destacado: true }
  ]);
  
  const [nuevoEvento, setNuevoEvento] = useState({
    hora: '',
    nombre: '',
    premio: '',
    buyin: '',
    destacado: false
  });

  useEffect(() => {
    if (canvasRef.current && !fabricCanvasRef.current) {
      fabricCanvasRef.current = new Canvas(canvasRef.current, {
        width: 1080,
        height: 1920,
        backgroundColor: '#0a0a1a'
      });
    }
    
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, []);

  const generarFlyer = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    canvas.clear();
    canvas.backgroundColor = '#0a0a1a';
    
    // Fondo con gradiente
    const gradient = new Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 1920
      },
      colorStops: [
        { offset: 0, color: '#1a0a2e' },
        { offset: 0.5, color: '#16213e' },
        { offset: 1, color: '#0f0a1e' }
      ]
    });
    
    canvas.backgroundColor = gradient;
    
    // Decoración superior (líneas brillantes)
    for (let i = 0; i < 5; i++) {
      const line = new Line([0, 50 + i * 30, 1080, 100 + i * 30], {
        stroke: `rgba(0, 255, 255, ${0.1 - i * 0.02})`,
        strokeWidth: 2,
        selectable: false
      });
      canvas.add(line);
    }
    
    // Logo simulado (UnitedPoker)
    const logoRect = new Rect({
      left: 80,
      top: 60,
      width: 280,
      height: 100,
      fill: 'rgba(139, 0, 0, 0.8)',
      stroke: '#ff0000',
      strokeWidth: 3,
      rx: 10,
      ry: 10,
      selectable: false
    });
    canvas.add(logoRect);
    
    const logoText = new FabricText('UNITEDPOKER', {
      left: 220,
      top: 95,
      fontSize: 32,
      fill: 'white',
      fontFamily: 'Arial Black',
      fontWeight: 'bold',
      originX: 'center',
      originY: 'center',
      selectable: false
    });
    canvas.add(logoText);
    
    // Fecha (esquina superior derecha)
    const fechaGroup = new Group([
      new Rect({
        width: 300,
        height: 140,
        fill: 'rgba(100, 20, 150, 0.9)',
        stroke: '#00ffff',
        strokeWidth: 3,
        rx: 15,
        ry: 15
      }),
      new FabricText(fecha.diaSemana, {
        fontSize: 20,
        fill: '#00ffff',
        fontFamily: 'Arial',
        top: 15,
        left: 150,
        originX: 'center'
      }),
      new FabricText(fecha.dia, {
        fontSize: 70,
        fill: 'white',
        fontFamily: 'Arial Black',
        fontWeight: 'bold',
        top: 45,
        left: 150,
        originX: 'center'
      }),
      new FabricText(fecha.mes, {
        fontSize: 18,
        fill: '#00ffff',
        fontFamily: 'Arial',
        top: 115,
        left: 150,
        originX: 'center'
      })
    ], {
      left: 710,
      top: 50,
      selectable: false
    });
    canvas.add(fechaGroup);
    
    // Título principal
    const tituloText = new FabricText('GRILLA DE TORNEOS', {
      left: 540,
      top: 220,
      fontSize: 42,
      fill: '#00ffff',
      fontFamily: 'Arial Black',
      fontWeight: 'bold',
      originX: 'center',
      selectable: false,
      shadow: new Shadow({
        color: '#00ffff',
        blur: 20
      })
    });
    canvas.add(tituloText);
    
    // Headers de columnas
    const headerY = 300;
    const headers = [
      { text: 'HORA', x: 120, w: 'normal' },
      { text: 'TORNEO', x: 400, w: 'bold' },
      { text: 'PREMIO', x: 720, w: 'bold' },
      { text: 'BUY-IN', x: 950, w: 'normal' }
    ];
    
    headers.forEach(header => {
      canvas.add(new FabricText(header.text, {
        left: header.x,
        top: headerY,
        fontSize: 24,
        fill: '#888888',
        fontFamily: 'Arial',
        fontWeight: header.w,
        originX: 'center',
        selectable: false
      }));
    });
    
    // Línea separadora
    canvas.add(new Line([60, headerY + 40, 1020, headerY + 40], {
      stroke: 'rgba(0, 255, 255, 0.3)',
      strokeWidth: 2,
      selectable: false
    }));
    
    // Lista de eventos
    let yPos = headerY + 70;
    
    eventos.forEach((evento, index) => {
      const color = evento.destacado ? '#00ffff' : '#00ccff';
      const bgColor = evento.destacado ? 'rgba(0, 255, 255, 0.1)' : 'transparent';
      const fontSize = evento.destacado ? 32 : 28;
      
      // Fondo destacado
      if (evento.destacado) {
        canvas.add(new Rect({
          left: 60,
          top: yPos - 12,
          width: 960,
          height: 68,
          fill: bgColor,
          stroke: color,
          strokeWidth: 2,
          rx: 8,
          selectable: false
        }));
      }
      
      // Hora
      canvas.add(new FabricText(evento.hora, {
        left: 120,
        top: yPos,
        fontSize: fontSize - 2,
        fill: 'white',
        fontFamily: 'Arial',
        originX: 'center',
        selectable: false
      }));
      
      // Nombre del torneo
      canvas.add(new FabricText(evento.nombre, {
        left: 250,
        top: yPos,
        fontSize: fontSize,
        fill: 'white',
        fontFamily: 'Arial',
        fontWeight: evento.destacado ? 'bold' : 'normal',
        selectable: false
      }));
      
      // Premio
      const premioText = new FabricText(evento.premio.toLocaleString('es-AR'), {
        left: 720,
        top: yPos,
        fontSize: fontSize + 4,
        fill: color,
        fontFamily: 'Arial Black',
        fontWeight: 'bold',
        originX: 'center',
        selectable: false
      });
      canvas.add(premioText);
      
      // PP
      canvas.add(new FabricText('PP', {
        left: 830,
        top: yPos + 5,
        fontSize: 18,
        fill: color,
        fontFamily: 'Arial',
        selectable: false
      }));
      
      // Buy-in
      canvas.add(new FabricText(evento.buyin.toString(), {
        left: 950,
        top: yPos,
        fontSize: fontSize - 2,
        fill: '#888888',
        fontFamily: 'Arial',
        originX: 'center',
        selectable: false
      }));
      
      yPos += evento.destacado ? 85 : 70;
    });
    
    // Footer
    const footerY = 1800;
    canvas.add(new Rect({
      left: 60,
      top: footerY,
      width: 960,
      height: 80,
      fill: 'rgba(100, 20, 150, 0.8)',
      stroke: '#00ffff',
      strokeWidth: 2,
      rx: 10,
      selectable: false
    }));
    
    canvas.add(new FabricText('LA UNIÓN MÁS GRANDE', {
      left: 540,
      top: footerY + 40,
      fontSize: 36,
      fill: 'white',
      fontFamily: 'Arial Black',
      fontWeight: 'bold',
      originX: 'center',
      originY: 'center',
      selectable: false
    }));
    
    canvas.renderAll();
    toast.success('Flyer generado correctamente');
  };

  const descargarFlyer = () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) {
      toast.error('Primero genera el flyer');
      return;
    }

    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: 2 // Alta resolución
    });

    const link = document.createElement('a');
    link.download = `UnitedPoker-${fecha.dia}-${fecha.mes}.png`;
    link.href = dataURL;
    link.click();
    
    toast.success('Flyer descargado');
  };

  const agregarEvento = () => {
    if (!nuevoEvento.hora || !nuevoEvento.nombre || !nuevoEvento.premio || !nuevoEvento.buyin) {
      toast.error('Completa todos los campos del evento');
      return;
    }

    setEventos([...eventos, {
      hora: nuevoEvento.hora,
      nombre: nuevoEvento.nombre.toUpperCase(),
      premio: parseInt(nuevoEvento.premio),
      buyin: parseInt(nuevoEvento.buyin),
      destacado: nuevoEvento.destacado
    }]);

    setNuevoEvento({ hora: '', nombre: '', premio: '', buyin: '', destacado: false });
    toast.success('Evento agregado');
  };

  const eliminarEvento = (index) => {
    setEventos(eventos.filter((_, i) => i !== index));
    toast.success('Evento eliminado');
  };

  const toggleDestacado = (index) => {
    const nuevosEventos = [...eventos];
    nuevosEventos[index].destacado = !nuevosEventos[index].destacado;
    setEventos(nuevosEventos);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-8 h-8 text-purple-500" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Flyers IA - UnitedPoker
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Genera flyers profesionales automáticamente con tus eventos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel de configuración */}
        <div className="space-y-6">
          {/* Fecha */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Fecha del Flyer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Día</Label>
                  <Input 
                    value={fecha.dia} 
                    onChange={(e) => setFecha({...fecha, dia: e.target.value})}
                    placeholder="30"
                  />
                </div>
                <div>
                  <Label>Día Semana</Label>
                  <Input 
                    value={fecha.diaSemana} 
                    onChange={(e) => setFecha({...fecha, diaSemana: e.target.value.toUpperCase()})}
                    placeholder="JUEVES"
                  />
                </div>
                <div>
                  <Label>Mes</Label>
                  <Input 
                    value={fecha.mes} 
                    onChange={(e) => setFecha({...fecha, mes: e.target.value.toUpperCase()})}
                    placeholder="OCTUBRE"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agregar evento */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Agregar Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Hora</Label>
                  <Input 
                    value={nuevoEvento.hora}
                    onChange={(e) => setNuevoEvento({...nuevoEvento, hora: e.target.value})}
                    placeholder="15:00"
                  />
                </div>
                <div>
                  <Label>Nombre Torneo</Label>
                  <Input 
                    value={nuevoEvento.nombre}
                    onChange={(e) => setNuevoEvento({...nuevoEvento, nombre: e.target.value})}
                    placeholder="BOMB PKO"
                  />
                </div>
                <div>
                  <Label>Premio (PP)</Label>
                  <Input 
                    type="number"
                    value={nuevoEvento.premio}
                    onChange={(e) => setNuevoEvento({...nuevoEvento, premio: e.target.value})}
                    placeholder="27000"
                  />
                </div>
                <div>
                  <Label>Buy-in</Label>
                  <Input 
                    type="number"
                    value={nuevoEvento.buyin}
                    onChange={(e) => setNuevoEvento({...nuevoEvento, buyin: e.target.value})}
                    placeholder="180"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={nuevoEvento.destacado}
                  onChange={(e) => setNuevoEvento({...nuevoEvento, destacado: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label>Evento destacado</Label>
              </div>
              <Button onClick={agregarEvento} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Evento
              </Button>
            </CardContent>
          </Card>

          {/* Lista de eventos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Eventos ({eventos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {eventos.map((evento, index) => (
                  <div 
                    key={index} 
                    className={`p-3 rounded-lg border-2 ${
                      evento.destacado 
                        ? 'border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20' 
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="font-mono text-sm">{evento.hora}</span>
                          <span className="font-bold">{evento.nombre}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            Premio: {evento.premio.toLocaleString()} PP
                          </span>
                          <span>Buy-in: {evento.buyin}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant={evento.destacado ? "default" : "outline"}
                          onClick={() => toggleDestacado(index)}
                        >
                          <Trophy className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => eliminarEvento(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Botones de acción */}
          <div className="flex gap-4">
            <Button 
              onClick={generarFlyer} 
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              size="lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Generar Flyer
            </Button>
            <Button 
              onClick={descargarFlyer} 
              variant="outline"
              size="lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Descargar
            </Button>
          </div>
        </div>

        {/* Preview del canvas */}
        <Card className="lg:sticky lg:top-6">
          <CardHeader>
            <CardTitle>Preview del Flyer</CardTitle>
            <CardDescription>
              Vista previa en tiempo real - 1080x1920px
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-auto">
              <div className="mx-auto" style={{ width: '540px' }}>
                <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', border: '2px solid #ccc', borderRadius: '8px' }} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
